/**
 * Lampyr BugTracker widget.
 *
 * Loads the script once after sign-in and hands it the current user. The key is
 * an INGEST key — the worst it permits is filing reports into our own tracker —
 * so it ships in the bundle deliberately. Do not build a proxy to hide it.
 *
 * Four things here are load-bearing, all learned the hard way per the setup doc:
 *
 *  1. `window.__btInited` rather than a module flag. Vite's HMR re-runs module
 *     side effects, and without a global guard you get two bug buttons in dev
 *     and eventually a duplicate report.
 *  2. `?v=` on the script src, so a long-lived browser session cannot keep an
 *     old widget against a new app build.
 *  3. try/catch around init. A tracker that breaks the header is worse than no
 *     tracker at all.
 *  4. Theme the `.bt-w-ov` report form, not just the button. Both our apps are
 *     dark; the widget's form defaults to light and is unreadable against them.
 *     This is the step that is easy to skip because you never see the form
 *     unless you open it.
 */

/**
 * Our own bug mark, stroked with currentColor so it takes the theme from the
 * button it sits in rather than shipping its own palette.
 */
const BUG_ICON = [
  '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"',
  ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">',
  '<path d="M8 2l1.88 1.88"/><path d="M14.12 3.88L16 2"/>',
  '<path d="M9 7.13V6a3 3 0 1 1 6 0v1.13"/>',
  '<path d="M12 20a6 6 0 0 0 6-6v-3a4 4 0 0 0-4-4h-4a4 4 0 0 0-4 4v3a6 6 0 0 0 6 6z"/>',
  '<path d="M12 20v-9"/><path d="M6.53 9C4.6 8.8 3 7.1 3 5"/>',
  '<path d="M6 13H2"/><path d="M3 21c0-2.1 1.7-3.9 3.8-4"/>',
  '<path d="M20.97 5c0 2.1-1.6 3.8-3.5 4"/><path d="M22 13h-4"/>',
  '<path d="M17.2 17c2.1.1 3.8 1.9 3.8 4"/>',
  '</svg>',
].join('');

const SCRIPT_ID = 'bugtracker-widget-js';
const THEME_ID = 'bt-w-theme';

interface BugTrackerWindow {
  BugTrackerWidget?: { init: (cfg: Record<string, unknown>) => { close?: () => void } | undefined };
  __btInited?: boolean;
}

export interface BugTrackerUser {
  uid: string;
  email: string | null;
  displayName?: string | null;
}

/** Matches our dark palette. Without this the report form is light-on-dark. */
const THEME_CSS = `
/* Every selector is prefixed with :root purely to raise specificity above the
   widget's own sheet. The widget injects its stylesheet during init() — after
   ours — so an equal-specificity rule silently loses on source order. That is
   why the dropdown stayed white while the report form themed correctly: the
   form rules happened to be two classes deep and won, the menu rules were one
   class deep and lost. */

/* The dropdown — the first thing you see on click, and the part that was
   still rendering white-on-white in dark mode. */
:root .bt-w-menu  { background:var(--surface); border:1px solid var(--border-hi);
                    box-shadow:0 12px 40px rgba(0,0,0,.45); }
:root .bt-w-item  { color:var(--text); }
:root .bt-w-item:hover { background:var(--surface-2); }

/* Report form */
:root .bt-w-ov     { background:rgba(4,8,16,.62); }
:root .bt-w-card   { background:var(--surface); color:var(--text);
                     border:1px solid var(--border-hi);
                     box-shadow:0 20px 60px rgba(0,0,0,.45); }
:root .bt-w-h      { color:var(--text); }
:root .bt-w-in     { background:var(--bg); border-color:var(--border-hi); color:var(--text); }
:root .bt-w-in::placeholder { color:var(--text-muted); }
:root .bt-w-flabel { color:var(--text-dim); }
:root .bt-w-fhint  { color:var(--text-muted); }
:root .bt-w-file   { color:var(--text); }
:root .bt-w-btn2   { background:var(--surface-2); border-color:var(--border-hi); color:var(--text); }
:root .bt-w-btn2:hover { border-color:var(--blue); color:var(--blue); }
:root .bt-w-btn2.bt-w-primary,
:root .bt-w-primary { background:var(--blue); border-color:var(--blue); color:#fff; }

/* Consent banner: its own blue is fine, but the decline button is near
   invisible against it in either theme. */
:root .bt-w-bannerno { color:#fff; border-color:rgba(255,255,255,.55); }
`;

/**
 * @param apiKey  the project's ingest key
 * @param user    the signed-in user; nothing is initialised without one, because
 *                an anonymous report tells you nothing about who hit the bug
 * @param version app version, used for the cache-bust and reported with issues
 */
export function initBugTracker(apiKey: string, user: BugTrackerUser | null, version: string): void {
  if (typeof document === 'undefined') return;
  if (!apiKey || !user?.uid) return;

  const w = window as unknown as BugTrackerWindow;

  const doInit = () => {
    if (!w.BugTrackerWidget || w.__btInited) return;
    try {
      w.BugTrackerWidget.init({
        apiKey,
        userId: user.uid,
        user: {
          email: user.email ?? '',
          displayName: user.displayName || user.email || 'User',
        },
        app: { version, environment: 'production' },
        // Mount into the sidebar icon row rather than letting the widget float
        // its own button over the page.
        mount: '#bugtracker-mount',
        // Our mark and our button class, so it inherits the theme rather than
        // carrying its own colours.
        icon: BUG_ICON,
        buttonClass: 'util-btn',
        // Session replay stays off. It records our own users, which makes us the
        // data controller for whatever it captures — a decision to take
        // deliberately, with the privacy policy updated, not by default.
      });
      // Appended AFTER init(), because the widget injects its own stylesheet
      // during init. Source order decides ties, so ours has to land last.
      if (!document.getElementById(THEME_ID)) {
        const st = document.createElement('style');
        st.id = THEME_ID;
        st.textContent = THEME_CSS;
        document.head.appendChild(st);
      }

      w.__btInited = true;
    } catch {
      /* never break the app over a bug reporter */
    }
  };

  if (w.BugTrackerWidget) { doInit(); return; }

  const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
  if (existing) {
    existing.addEventListener('load', doInit);
    doInit();
    return;
  }

  const s = document.createElement('script');
  s.id = SCRIPT_ID;
  s.src = `https://bugtracker.lampyrlabs.com/bugtracker-widget.js?v=${encodeURIComponent(version)}`;
  s.async = true;
  s.addEventListener('load', doInit);
  document.body.appendChild(s);
}

/** Platform admin project key. Ingest-only - safe in the bundle. */
export const ADMIN_BUGTRACKER_KEY = 'bt_782026da26bcc93b8d14b5b52f56050e933c85663646ee65';
